import {
  Box,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
} from "@mui/material";
import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch } from "../../../../app/hooks";
import AddButton from "../../../../common/components/functional/addButton/AddButton";
import { ConfirmationDialog } from "../../../../common/components/functional/confirmationDialog/ConfirmationDialog";
import DialogComponent from "../../../../common/components/functional/dialogs/DialogComponent";
import OverlaySpinner from "../../../../common/components/functional/overlaySpinner/OverlaySpinner";
import { ReactComponent as RemoveFilled } from "../../../../common/icons/delete.svg";
import { ReactComponent as EditFilled } from "../../../../common/icons/editFilled.svg";
import { formatDateStringForUI } from "../../../customer/contract/helpers/commonUtils";
import { TalentUnionPayment } from "../../../talents/talentAPI";
import {
  createTalentUnionPayment,
  deleteTalentUnionPayment,
  fetchTalentUnionPayments,
  updateTalentUnionPayment,
} from "../../../talents/talentsSlice";
import { AccessRightContext } from "../../../user/AccessRightProvider";
import AddEditModal from "./AddEditModal";

export interface Union {
  id: string;
  unionName: string;
  employer: string;
  startDate: string;
  endDate: string;
}

interface Props {
  talentId: string;
  isReadOnly: boolean;
}

const UnionFee = ({ talentId, isReadOnly }: Props) => {
  const [editedItem, setEditedItem] = useState<TalentUnionPayment>();

  const [talentUnionPayments, seTalentUnionPayments] = useState<
    TalentUnionPayment[]
  >([]);

  const [confirmDeleteDialog, setConfirmDeleteDialog] = useState<{
    isOpen: boolean;
    idToDelete: string | null;
  }>({ isOpen: false, idToDelete: null });

  const [modalAction, setModalAction] = useState<string>("");

  const { t } = useTranslation();

  const [loading, setLoading] = useState<boolean>(false);

  const dispatch = useAppDispatch();

  const { canEditUnionPayment } = useContext(AccessRightContext);

  useEffect(() => {
    // Fetch talent union payments
    if (talentId) {
      dispatch(fetchTalentUnionPayments(talentId)).then((res: any) => {
        if (res.meta.requestStatus === "fulfilled") {
          seTalentUnionPayments([...res.payload]);
        }
      });
    }
  }, [dispatch, talentId]); // Include dependencies in the dependency array

  const closeEditingModal = () => {
    setModalAction("");
    setEditedItem(undefined);
  };

  const onEditSubmit = (payment: TalentUnionPayment) => {
    setLoading(true);
    if (modalAction === "new") {
      dispatch(
        createTalentUnionPayment({
          talentId: talentId,
          tradeUnionId: payment.tradeUnionId,
          eezyCompanyId: payment.eezyCompanyId,
          validFrom: payment.validFrom,
          validTo: payment.validTo,
        }),
      ).then((res: any) => {
        if (res.meta.requestStatus === "fulfilled") {
          seTalentUnionPayments([
            ...talentUnionPayments,
            {
              ...payment,
              id: res.payload[0].id,
            },
          ]);
          closeEditingModal();
          setLoading(false);
        } else {
          setLoading(false);
        }
      });
    } else {
      dispatch(updateTalentUnionPayment({ ...payment })).then((res: any) => {
        if (res.meta.requestStatus === "fulfilled") {
          seTalentUnionPayments([
            ...talentUnionPayments.map((item) => {
              if (item.id === payment.id) {
                return payment;
              } else {
                return item;
              }
            }),
          ]);
          closeEditingModal();
          setLoading(false);
        } else {
          setLoading(false);
        }
      });
    }
  };

  const handleConfirmDelete = () => {
    const id = confirmDeleteDialog.idToDelete;

    id &&
      dispatch(deleteTalentUnionPayment(id)).then((res: any) => {
        if (res.meta.requestStatus === "fulfilled") {
          seTalentUnionPayments([
            ...talentUnionPayments.filter((item) => item.id !== id),
          ]);
          // Close the dialog
          setConfirmDeleteDialog({ isOpen: false, idToDelete: null });
        }
      });
  };

  const sortedTalentUnionPayments = [...talentUnionPayments].sort((a, b) => {
    // Prioritize ongoing items (without a validTo date) to appear at the top
    if (!a.validTo && b.validTo) return -1;
    if (a.validTo && !b.validTo) return 1;

    // For items with a validTo date, sort them so the most recent (or upcoming) dates are first
    const comparisonResult = (b.validTo || "").localeCompare(a.validTo || "");
    if (comparisonResult !== 0) return comparisonResult;

    // If validTo dates are the same or both missing, then sort by validFrom with newest first as a tiebreaker
    return (b.validFrom || "").localeCompare(a.validFrom || "");
  });

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        background: "#FFFFFF",
        borderRadius: "8px",
        marginTop: "16px",
        padding: "0px 16px 0px 0px",
        height: "calc(100% - 16px)",
      }}
    >
      <OverlaySpinner isLoading={loading} />
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <DialogTitle>
          <Typography
            variant="header"
            sx={{ fontWeight: 600, fontSize: "14px" }}
          >
            {t("talentProfile:unionMembershipFee").toUpperCase()}
          </Typography>
        </DialogTitle>
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          padding: "0px 0px 16px 16px",
          fontSize: "12px",
          maxHeight: "250px",
          overflowY: "auto",
        }}
      >
        <TableContainer>
          <Table>
            <TableBody>
              <TableRow>
                <TableCell sx={{ paddingLeft: "0px", fontSize: "12px" }}>
                  {t("talentProfile:unionName")}
                </TableCell>
                <TableCell sx={{ paddingLeft: "0px", fontSize: "12px" }}>
                  {t("talentProfile:employer")}
                </TableCell>
                <TableCell sx={{ paddingLeft: "0px", fontSize: "12px" }}>
                  {t("talentProfile:startDate")}
                </TableCell>
                <TableCell sx={{ paddingLeft: "0px", fontSize: "12px" }}>
                  {t("talentProfile:endDate")}
                </TableCell>
                <TableCell />
              </TableRow>
              {sortedTalentUnionPayments?.map(
                (unionPayment: TalentUnionPayment, index: number) => (
                  <TableRow key={index}>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        paddingLeft: "0px",
                        width: "80px",
                      }}
                    >
                      {unionPayment?.tradeUnionAcronym}
                    </TableCell>
                    <TableCell sx={{ paddingLeft: "0px" }}>
                      {unionPayment?.eezyCompanyName}
                    </TableCell>
                    <TableCell sx={{ paddingLeft: "0px" }}>
                      {unionPayment?.validFrom
                        ? formatDateStringForUI(unionPayment?.validFrom)
                        : ""}
                    </TableCell>
                    <TableCell sx={{ paddingLeft: "0px", width: "120px" }}>
                      {unionPayment?.validTo
                        ? formatDateStringForUI(unionPayment?.validTo)
                        : ""}
                    </TableCell>
                    <TableCell
                      sx={{
                        paddingLeft: "0px",
                        width: "70px",
                      }}
                    >
                      {canEditUnionPayment && (
                        <>
                          <EditFilled
                            onClick={() => {
                              setEditedItem({ ...unionPayment });
                              setModalAction("edit");
                            }}
                            style={{
                              width: "16px",
                              height: "16px",
                              cursor: "pointer",
                              marginRight: "16px",
                            }}
                          />
                          <RemoveFilled
                            onClick={() =>
                              setConfirmDeleteDialog({
                                isOpen: true,
                                idToDelete: unionPayment.id,
                              })
                            }
                            style={{
                              width: "16px",
                              height: "16px",
                              cursor: "pointer",
                            }}
                          />
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                ),
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {canEditUnionPayment && (
          <Box
            sx={{
              marginTop: "16px",
              alignSelf: "flex-start",
            }}
          >
            <AddButton
              variant="outlined"
              label={t("talentProfile:addUnionMembershipFee")}
              onClick={() => setModalAction("new")}
              disabled={isReadOnly}
            />
          </Box>
        )}
      </Box>
      {
        <DialogComponent
          open={modalAction !== ""}
          onClose={closeEditingModal}
          autoWidth
          DialogContentProps={{
            sx: {
              padding: "0px",
            },
          }}
        >
          <AddEditModal
            onEditSubmit={onEditSubmit}
            onEditCancel={closeEditingModal}
            item={editedItem}
            modalAction={modalAction}
            talentId={talentId}
            loading={loading}
            setLoading={setLoading}
          />
        </DialogComponent>
      }
      <ConfirmationDialog
        title={t("talentProfile:unionPaymentDeleteConfirmationTitle")}
        contentText={t("talentProfile:unionPaymentDeleteConfirmationContext")}
        confirmText={t("hourEntries:entriesConfirmButton")}
        cancelText={t("hourEntries:entriesCancelButton")}
        isOpen={confirmDeleteDialog.isOpen}
        onCancel={() =>
          setConfirmDeleteDialog({ isOpen: false, idToDelete: null })
        }
        onConfirm={() => handleConfirmDelete()}
      />
    </Box>
  );
};

export default UnionFee;
